// @ts-nocheck TODO: Fix this
import { MDXComponents, MDXStyles } from '@bacons/mdx';
import React, { useCallback } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { BlockQuote } from '@expo/html-elements';
import { Prism, Highlight, themes } from 'prism-react-renderer';
import { clsx as cn } from 'clsx';
import { Link } from 'expo-router';
import { toast } from 'sonner';

import { YoutubeEmbed } from './embeds/YouTube';
import { Embed } from './embeds';
import { GithubCard } from './embeds/GithubCard';
import { ReanimatePromo } from './embeds/ReanimatePromo';

import { Colors } from '@/constants/blog-colors';
import { resolveAssetUri } from '@/utils/resolveMetroAsset';
import { Title } from '@/components/PostTitle';
import ConstantsColors from '@/constants/Colors';
import { Image as MarkdownImage } from '@/components/markdown/image';

(typeof global !== 'undefined' ? global : window).Prism = Prism;

import('prismjs/components/prism-shell-session');
import('prismjs/components/prism-json');
import('prismjs/components/prism-json5');
import('prismjs/components/prism-css-extras.min');

// src/themes/dracula.ts
const terminalTheme = {
  plain: {
    color: '#F8F8F2',
    backgroundColor: '#000000',
  },
  styles: [
    {
      types: ['comment', 'command'],
      style: {
        color: 'rgb(98, 114, 164)',
      },
    },
  ],
};

const fontSize = '0.95em'; // Define a reusable font size

const themePlus = {
  ...themes.palenight,
  plain: {
    ...themes.palenight.plain,
    backgroundColor: ConstantsColors.codeBox, // Dark background
    fontSize: fontSize, // Use the defined font size
  },
  styles: [
    // Default text color
    {
      ...themes.palenight.styles[0],
      style: {
        ...themes.palenight.styles[0].style,
        color: '#f2f5f7', // Light gray text
        fontSize: fontSize, // Reuse the defined font size
      },
    },
    // Adding specific styles for different types of code
    {
      types: ['keyword', 'variable'],
      style: {
        color: '#c678dd', // Purple for keywords and variables
        fontSize: fontSize, // Reuse the defined font size
      },
    },
    {
      types: ['string', 'char'],
      style: {
        color: '#e06c75', // Red for strings
        fontSize: fontSize, // Reuse the defined font size
      },
    },
    {
      types: ['number'],
      style: {
        color: '#d19a66', // Orange for numbers
        fontSize: fontSize, // Reuse the defined font size
      },
    },
    {
      types: ['comment'],
      style: {
        color: '#5c6370', // Darker gray for comments
        fontSize: fontSize, // Reuse the defined font size
      },
    },
    {
      types: ['function', 'class-name'],
      style: {
        color: '#4b9fe4', // Light blue for functions and class names
        fontSize: fontSize, // Reuse the defined font size
      },
    },
    {
      types: ['attr-name'],
      style: {
        color: '#e5c07b', // Light yellow for attributes
        fontSize: fontSize, // Reuse the defined font size
      },
    },
    {
      types: ['regex', 'important'],
      style: {
        color: '#56b6c2', // Cyan for regex
        fontSize: fontSize, // Reuse the defined font size
      },
    },
    {
      types: ['punctuation', 'operator'],
      style: {
        color: '#abb2bf', // Light gray for punctuation
        fontSize: fontSize, // Reuse the defined font size
      },
    },
  ],
};

function CopyIcon(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      role="img"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );
}

const remapLanguages: Record<string, string> = {
  'objective-c': 'objc',
  sh: 'bash',
  shell: 'shell-session',
  rb: 'ruby',
  json: 'json5',
  javascript: 'js',
  typescript: 'ts',
};

function Kbd({ children }) {
  const keys = children.split('');
  return (
    <>
      {keys.map((key, i) => (
        <React.Fragment key={i}>
          <kbd
            className="px-1 min-w-5 bg-surface-code border border-b-2 rounded border-border"
            style={{
              boxShadow: '0 0.1rem 0 1px #3a3f42',
            }}
          >
            {key}
          </kbd>
          {i < keys.length - 1 ? ' + ' : null}
        </React.Fragment>
      ))}
    </>
  );
}

// Diff line styles using theme colors (success/danger from tailwind.config.js)
const diffStyles = {
  added: {
    backgroundColor: 'rgba(16, 185, 129, 0.12)', // success-500 with low opacity
    borderLeft: '3px solid #10B981', // success-500
  },
  removed: {
    backgroundColor: 'rgba(239, 68, 68, 0.12)', // danger-500 with low opacity
    borderLeft: '3px solid #EF4444', // danger-500
  },
  neutral: {
    borderLeft: '3px solid transparent',
  },
};

function getDiffLineType(line: string): 'added' | 'removed' | 'neutral' | null {
  if (line.startsWith('+ ') || line === '+') return 'added';
  if (line.startsWith('- ') || line === '-') return 'removed';
  return null;
}

function stripDiffPrefix(line: string): string {
  if (line.startsWith('+ ') || line.startsWith('- ')) {
    return line.slice(2);
  }
  if (line === '+' || line === '-') {
    return '';
  }
  return line;
}

function CodeHighlight(props: {
  children: string;
  // language-ts
  className: string;
  // "app.config.ts"
  metastring: string;
  // "html.pre"
  parentName: string;
}) {
  let lang = props.className?.slice(9).toLowerCase() ?? 'txt';
  const isTerminal = ['terminal', 'term'].includes(lang.toLowerCase());
  const isDiff = props.metastring?.includes('diff') || lang === 'diff';

  if (isTerminal) {
    lang = 'shell-session';
  }

  if (lang in remapLanguages) {
    lang = remapLanguages[lang];
  }

  // For diff mode, extract the actual language if specified (e.g., "tsx diff" -> "tsx")
  let actualLang = lang;
  if (isDiff && props.metastring) {
    const langMatch = props.metastring.match(/^(\w+)\s+diff/);
    if (langMatch) {
      actualLang = langMatch[1];
      if (actualLang in remapLanguages) {
        actualLang = remapLanguages[actualLang];
      }
    }
  }

  let title = !props.metastring
    ? ''
    : (props.metastring.match(/title="(.*)"/)?.[1] ?? props.metastring);

  if (isTerminal && !title) {
    title = 'Terminal';
  }

  const rawCode = props.children.trim();

  // For diff mode, process lines to detect +/- prefixes
  const lines = rawCode.split('\n');
  const diffLineTypes = isDiff ? lines.map(getDiffLineType) : [];
  const hasDiffMarkers = diffLineTypes.some((type) => type !== null);

  // Strip diff prefixes for syntax highlighting, but only if we have diff markers
  const code =
    isDiff && hasDiffMarkers ? lines.map(stripDiffPrefix).join('\n') : rawCode;

  const handleCopy = useCallback(() => {
    toast.success('Snippet copied to clipboard');
    // Copy the clean code without diff markers
    navigator.clipboard.writeText(code);
  }, [code]);

  return (
    <div className="rounded-2xl transition-shadow hover:shadow-xl mt-0 group relative max-w-[calc(100vw-2.9rem)]">
      <div
        data-lang={lang}
        className="rounded-2xl border border-white/10 relative overflow-hidden"
      >
        <button
          onClick={handleCopy}
          className={cn(
            'absolute flex justify-between items-center p-3 border border-white/10 hover:border-white/60 hover:bg-black/20 hover:shadow-lg rounded-xl cursor-pointer  focus:border-green-400 focus:scale-95 opacity-0 group-hover:opacity-100 transition-all',
            code.split('\n').length > 1 ? 'right-2 top-2' : 'right-1 top-1'
          )}
        >
          <CopyIcon className="w-5 text-subtle-50" />
        </button>

        <Highlight
          theme={isTerminal ? terminalTheme : themePlus}
          code={code}
          language={isDiff && hasDiffMarkers ? actualLang : lang}
        >
          {({ style, tokens, getLineProps, getTokenProps }) => (
            <pre
              style={{
                ...style,
                margin: 0,
              }}
              className={cn('p-4 mt-0 overflow-auto', isTerminal && 'bg-black')}
            >
              <code className="inline-block min-w-full">
                {tokens.map((line, i) => {
                  const diffType =
                    isDiff && hasDiffMarkers ? diffLineTypes[i] : null;
                  const lineStyle = diffType
                    ? diffStyles[diffType]
                    : isDiff && hasDiffMarkers
                      ? diffStyles.neutral
                      : {};

                  return (
                    <div
                      key={i}
                      {...getLineProps({ line })}
                      className="whitespace-pre"
                      style={{
                        ...lineStyle,
                        marginLeft: isDiff && hasDiffMarkers ? '-1rem' : 0,
                        paddingLeft:
                          isDiff && hasDiffMarkers ? 'calc(1rem - 3px)' : 0,
                        marginRight: isDiff && hasDiffMarkers ? '-1rem' : 0,
                        paddingRight: isDiff && hasDiffMarkers ? '1rem' : 0,
                      }}
                    >
                      {line.map((token, key) => {
                        const tokenProps = getTokenProps({ token });
                        return <span key={key} {...tokenProps} />;
                      })}
                    </div>
                  );
                })}
              </code>
            </pre>
          )}
        </Highlight>
      </div>
    </div>
  );
}

type MarkdownThemeProps = {
  children: React.ReactNode;
  components?: Record<string, React.ComponentType<any>>;
};

const styles = StyleSheet.create({
  a: {
    fontSize: 'unset',
    fontFamily: 'var(--font-inter)',
  },
  standardLink: {
    textDecorationLine: 'none', // No underline by default
  },
  text: {
    fontFamily: 'var(--font-inter)',
    fontSize: 16,
    lineHeight: 24,
    color: Colors.paragraph,
  },
  code: {
    fontFamily: 'var(--font-source-code)',
    fontSize: 14,
  },
});

export function MarkdownTheme({ children, components }: MarkdownThemeProps) {
  const styles = StyleSheet.create({
    a: {
      fontSize: 'unset',
      fontFamily: 'var(--font-inter)',
    },
    standardLink: {
      textDecorationLine: 'none', // No underline by default
      transition: 'all 0.2s ease-in-out',
    },
  });

  return (
    <MDXStyles
      h1={{
        fontFamily: 'var(--font-inter)',
        fontSize: 32,
        color: Colors.h1,
      }}
      h2={{
        display: 'block',
        fontFamily: 'var(--font-inter)',
        marginTop: '1.5em',
        fontSize: '1.2em',
        marginBottom: '0.5em',
        lineHeight: '1.3333333',
        color: Colors.h2,
      }}
      h3={{
        fontFamily: 'var(--font-inter)',
        marginTop: '1em',
        fontSize: '1em',
        marginBottom: '0.5em',
        lineHeight: '1.3333333',
        color: Colors.h3,
      }}
      h4={{
        fontFamily: 'var(--font-inter)',
        fontSize: '0.95em',
        marginBottom: '0.5em',
        lineHeight: '1.3333333',
        color: Colors.h4,
      }}
      strong={{
        color: Colors.strong,
      }}
      code={{
        borderRadius: 2,
        color: Colors.code,
        backgroundColor: 'rgba(115, 125, 140, 0.17)',
        padding: 10,
        fontSize: 'unset',
        marginTop: 0,
      }}
      inlineCode={{
        borderRadius: 6,
        fontSize: '0.9em',
        fontFamily: 'var(--font-source-code)',
        color: '#e5c07b',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        paddingVertical: 3,
        paddingHorizontal: 7,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
      }}
      p={{
        fontFamily: 'var(--font-inter)',
        lineHeight: '1.85',
        fontSize: '.97rem',
        marginTop: 0,
        marginBottom: '1.25em',
        color: Colors.paragraph,
        display: 'block',
      }}
      blockquote={{
        fontFamily: 'var(--font-inter)',
        borderLeftWidth: 2,
        fontSize: 21,
        backgroundColor: Colors.blockquote,
        borderLeftColor: '#f2f5f7',
        padding: 23,
        paddingBottom: 8,
      }}
      ul={{
        marginBottom: '1.25em',
      }}
      ol={{
        marginBottom: '1.25em',
      }}
      img={{
        borderRadius: '1rem',
        marginBottom: '1.25em',
        minHeight: 180,
        maxHeight: 480,
      }}
      a={styles.a}
      li={{
        fontFamily: 'var(--font-inter)',
        fontSize: 16,
        lineHeight: 30,
        color: Colors.ul,
      }}
      hr={{
        paddingBottom: 10,
        marginBottom: 14,
        marginTop: 32,
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: 24,
      }}
    >
      <MDXComponents
        components={{
          Title,
          Kbd,
          YouTubeEmbed: YoutubeEmbed,
          Image: MarkdownImage,
          Embed,
          GithubCard,
          ReanimatePromo,
          ...components,
        }}
        code={CodeHighlight}
        pre={({ style, children }) => {
          return <pre style={style} children={children} className="mb-5" />;
        }}
        em={({
          firstChild,
          firstOfType,
          prevSibling,
          lastChild,
          parentName,
          ...props
        }) => <em {...props} />}
        p={({ style, children }) => {
          // NOTE(EvanBacon): Unclear why, but mdxjs is wrapping an image in a paragraph tag.
          const image = React.Children.toArray(children).find((child) => {
            return (
              typeof child === 'object' && 'props' in child && child.props.src
            );
          });
          if (image) {
            return <>{children}</>;
          }

          return <Text style={style} children={children} />;
        }}
        br={() => <br />}
        strong={({ style, children }) => {
          const localStyles = { fontWeight: '500' };
          const brand = getAnchorBrand(children);
          // Special branding for bold text containing "Pillar Valley".
          if (children.toString().match(/pillar valley/i)) {
            localStyles.color = '#F09458';
          }
          if (brand) {
            localStyles.color = '#fff';
            return (
              <div className="p-1 inline bg-surface-code border rounded border-border">
                <Text children={children} style={[style, localStyles]} />
              </div>
            );
          }
          return <Text children={children} style={[style, localStyles]} />;
        }}
        blockquote={({
          firstChild,
          lastChild,
          parentName,
          style,
          firstOfType,
          children,
          prevSibling,
          ...props
        }) => {
          return (
            <BlockQuote {...props} style={[style]}>
              {children}
            </BlockQuote>
          );
        }}
        a={({ href, children, style, className, ...props }) => {
          const brand =
            children?.props?.parentName === 'html.a' &&
            getAnchorBrand(children.props.children);

          return (
            <Link
              href={href}
              target={href.startsWith('http') ? '_blank' : undefined}
              style={[style, !brand && styles.standardLink]}
              className={cn(
                className,
                !brand &&
                  'text-text-link transition-colors duration-500 ease-out relative after:absolute after:bottom-0 after:left-0 after:h-px after:w-0 after:bg-text-linkHover/60 after:transition-all after:duration-500 after:ease-out hover:!text-text-linkHover hover:after:w-full'
              )}
              children={children}
            />
          );
        }}
        img={Img}
        li={({
          firstChild,
          lastChild,
          parentName,
          prevSibling,
          firstOfType,
          style,
          ...props
        }) => (
          <li
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              marginLeft: 8,
            }}
          >
            <View
              style={{
                userSelect: 'none',
                display: 'block',
                marginTop: 12,
                marginRight: 12,
                width: 6,
                height: 6,
                borderRadius: 3,
                backgroundColor: '#f2f5f7',
              }}
            />
            <Text {...props} style={[style, { display: 'block' }]} />
          </li>
        )}
        // ul={({ style, ...props }) => (
        //   <ul {...props} style={[{ marginBottom: '1rem' }, style]} />
        // )}
        hr={({ style }) => (
          <View
            style={[
              style,
              {
                borderBottomWidth: 0.5,
                borderBottomColor: '#f2f5f7',
                opacity: 0.2,
              },
            ]}
          />
        )}
      >
        {children}
      </MDXComponents>
    </MDXStyles>
  );
}

function Img({ src, style }) {
  const source = typeof src === 'string' ? { uri: src } : src;
  const srcUrl = resolveAssetUri(source);

  if (srcUrl.match(/\.mp4$/)) {
    return (
      <video
        src={srcUrl}
        style={style}
        controls
        autoPlay
        loop
        playsInline
        className="h-auto max-w-full overflow-clip object-contain"
      />
    );
  }
  return (
    <img
      src={srcUrl}
      style={style}
      className="h-auto max-w-full overflow-clip object-contain"
    />
  );
}

function getAnchorBrand(text: string) {
  if (text.toString().match(/^expo[-\s]?router$/i)) {
    return 'expo-router';
  }
  if (text.toString().match(/^sirius[-\s]?xm$/i)) {
    return 'sirusxm';
  }
  if (text.toString().match(/^lego$/i)) {
    return 'lego';
  }
  if (text.toString().match(/^(react-navigation|react navigation)$/i)) {
    return 'react-navigation';
  }
  if (text.toString().match(/^(expo|expo sdk)$/i)) {
    return 'expo';
  }
  return null;
}
